import {
  WORKPLACE_REQUEST_RELEASED_RESOURCE_ACTIONS,
  WORKPLACE_REQUEST_WORKFLOW_ACTIONS,
  WORKPLACE_REQUEST_WORKFLOW_CONTEXT_KEYS,
  WorkplaceRequestElement,
  WorkplaceRequestReleasedResourceAction,
  WorkplaceRequestWorkflow,
  WorkplaceRequestWorkflowRunAt,
  WorkplaceRequestWorkflowRunResult,
  WorkplaceRequestWorkflowValueSelector,
  workplaceRequestContextKeyFieldId
} from "@gettactic/api";

/**
 * Resolves a request type's desk-assignment workflows against one request, so
 * both the approver's "What happens on approval" panel and the submitter's
 * preview can say what the automation will actually do — with this person and
 * this desk, not in the abstract.
 *
 * This reads the workflow config directly rather than going through the
 * settings editor's model: the editor carries concerns this side has no use for
 * (drafts, sync plans, entries it can't represent), and a request only ever
 * needs the four `resources:` actions resolved against known values.
 *
 * Nothing here fetches: the caller supplies the ids it already holds, and
 * anything still unknown stays `null` so the UI can say so rather than guess.
 */

/** The values a workflow's selectors can point at on a single request. */
export type DeskAutomationContext = {
  /** Field answers by field id. A blank answer should be absent or empty. */
  valueByFieldId: Map<string, unknown>;
  submitterId: string | null;
  ownerId: string | null;
  /** The resource the request was filed against, when its location is one. */
  locationResourceId: string | null;
};

export type DeskAutomationStep = {
  workflowId: string;
  /** Whether the desk ends up held or freed. */
  action: "assign" | "release";
  mode: "assigned" | "dedicated";
  /** `null` when the answer it reads is still blank. */
  personId: string | null;
  resourceId: string | null;
  /**
   * Where each value comes from. Kept even once the value resolves, because a
   * blank answer still has to be described — and "the requested workspace" is
   * only right for a rule that actually reads the request's location.
   */
  personSource: "field" | "submitter" | "owner" | "fixed";
  resourceSource: "workspace" | "field" | "fixed";
  /** The date the run waits for, or `null` when it runs on approval. */
  runsOn: string | null;
  /**
   * What the type's rule says becomes of the desk this frees. It is a default,
   * not a verdict: an approver may replace it for one request.
   */
  releasedResourceAction: WorkplaceRequestReleasedResourceAction;
  enabled: boolean;
};

const RESOURCE_ACTIONS = {
  [WORKPLACE_REQUEST_WORKFLOW_ACTIONS.setPrimaryAssignment]: {
    action: "assign",
    mode: "assigned"
  },
  [WORKPLACE_REQUEST_WORKFLOW_ACTIONS.assignDedicatedUser]: {
    action: "assign",
    mode: "dedicated"
  },
  [WORKPLACE_REQUEST_WORKFLOW_ACTIONS.removePrimaryAssignment]: {
    action: "release",
    mode: "assigned"
  },
  [WORKPLACE_REQUEST_WORKFLOW_ACTIONS.unassignDedicatedUser]: {
    action: "release",
    mode: "dedicated"
  }
} as const satisfies Record<
  string,
  { action: DeskAutomationStep["action"]; mode: DeskAutomationStep["mode"] }
>;

/** A non-empty string answer, or `null` — ids are the only shape we read. */
const asId = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const resolveSelector = (
  selector: WorkplaceRequestWorkflowValueSelector | undefined,
  context: DeskAutomationContext
): string | null => {
  if (!selector) return null;
  if (selector.source === "fixed") return asId(selector.value);

  const keys = WORKPLACE_REQUEST_WORKFLOW_CONTEXT_KEYS;
  if (selector.key === keys.submitter) return context.submitterId;
  if (selector.key === keys.owner) return context.ownerId;
  if (selector.key === keys.resourceLocation) return context.locationResourceId;

  const fieldId = workplaceRequestContextKeyFieldId(selector.key);
  return fieldId ? asId(context.valueByFieldId.get(fieldId)) : null;
};

const personSourceOf = (
  selector: WorkplaceRequestWorkflowValueSelector | undefined
): DeskAutomationStep["personSource"] => {
  if (!selector) return "field";
  if (selector.source === "fixed") return "fixed";
  const keys = WORKPLACE_REQUEST_WORKFLOW_CONTEXT_KEYS;
  if (selector.key === keys.submitter) return "submitter";
  if (selector.key === keys.owner) return "owner";
  return "field";
};

const resourceSourceOf = (
  selector: WorkplaceRequestWorkflowValueSelector | undefined
): DeskAutomationStep["resourceSource"] => {
  if (!selector) return "field";
  if (selector.source === "fixed") return "fixed";
  return selector.key ===
    WORKPLACE_REQUEST_WORKFLOW_CONTEXT_KEYS.resourceLocation
    ? "workspace"
    : "field";
};

/**
 * The date a deferred run waits for. A fixed `at` is returned as configured; a
 * context one reads the request's own answer, which is blank until it is filled
 * in. `time`/`timezone` are deliberately ignored — this is a date, not the
 * exact instant, and stating a time we haven't resolved would be a lie.
 */
const resolveRunAt = (
  runAt: WorkplaceRequestWorkflowRunAt | undefined,
  context: DeskAutomationContext
): string | null => {
  if (!runAt) return null;
  if (runAt.source === "fixed") return runAt.at || null;
  const fieldId = workplaceRequestContextKeyFieldId(runAt.key);
  return fieldId ? asId(context.valueByFieldId.get(fieldId)) : null;
};

/**
 * Narrows a stored or picked value to one of the three actions. Core omits the
 * key when the rule leaves the freed desk alone, and rules written before the
 * setting existed have none. An action this build doesn't recognise reads as
 * "none" as well — describing a request as doing something we can't name would
 * be worse than describing it as doing nothing.
 */
export const readReleasedResourceAction = (
  action: string | undefined
): WorkplaceRequestReleasedResourceAction =>
  action === WORKPLACE_REQUEST_RELEASED_RESOURCE_ACTIONS.makeOpen ||
  action === WORKPLACE_REQUEST_RELEASED_RESOURCE_ACTIONS.makeUnavailable
    ? action
    : WORKPLACE_REQUEST_RELEASED_RESOURCE_ACTIONS.none;

/**
 * A stored answer as the rules read it. Core hands back a person answer as the
 * whole user it resolved, not the id that was written, so that one is folded
 * back to its id here — the selectors only ever compare ids.
 */
const fieldValueForRules = (value: unknown): unknown =>
  value && typeof value === "object" && "id" in value
    ? (value as { id: unknown }).id
    : value;

/**
 * The values one request offers the rules, read straight off it. Shared so the
 * approval box and the automation panel resolve the same rules against the
 * same request rather than each assembling their own idea of it.
 */
export const deskAutomationContextOf = (
  request: Pick<
    WorkplaceRequestElement,
    | "field_values"
    | "submitter_user"
    | "owner_user"
    | "location"
    | "resource_id"
  >
): DeskAutomationContext => ({
  valueByFieldId: new Map(
    (request.field_values ?? []).map((value) => [
      value.id,
      fieldValueForRules(value.value)
    ])
  ),
  submitterId: request.submitter_user?.id ?? null,
  ownerId: request.owner_user?.id ?? null,
  locationResourceId:
    request.location?.type === "resource"
      ? request.location.resource_id
      : request.resource_id
});

/**
 * The desk-assignment steps a request will run, in configuration order.
 * Child-ticket workflows and any action this doesn't know are left out.
 */
export const resolveDeskAutomation = (
  workflows: WorkplaceRequestWorkflow[] | undefined,
  context: DeskAutomationContext
): DeskAutomationStep[] =>
  (workflows ?? []).flatMap((workflow) => {
    const shape =
      RESOURCE_ACTIONS[workflow.action as keyof typeof RESOURCE_ACTIONS];
    if (!shape) return [];

    const config = workflow.config;
    if (!config || !("user" in config)) return [];

    return [
      {
        workflowId: workflow.id,
        action: shape.action,
        mode: shape.mode,
        personId: resolveSelector(config.user, context),
        resourceId: resolveSelector(config.resource, context),
        personSource: personSourceOf(config.user),
        resourceSource: resourceSourceOf(config.resource),
        runsOn: resolveRunAt(config.run_at, context),
        releasedResourceAction: readReleasedResourceAction(
          config.released_resource_action
        ),
        enabled: workflow.enabled
      }
    ];
  });

/** What to call a value that hasn't resolved, per where the rule reads it. */
const PERSON_PLACEHOLDER: Record<DeskAutomationStep["personSource"], string> = {
  field: "whoever is named on the form",
  submitter: "whoever submits it",
  owner: "the ticket owner",
  fixed: "a specific person"
};

const DESK_PLACEHOLDER: Record<DeskAutomationStep["resourceSource"], string> = {
  workspace: "the requested workspace",
  field: "the desk picked on the form",
  fixed: "a specific desk"
};

const capitalised = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

/**
 * The step written as a sentence. A value that isn't filled in yet is named by
 * the *source* the rule reads it from rather than hidden, so the reader can see
 * what is still missing — and is pointed at the right place to supply it.
 */
export const deskAutomationSentence = (
  step: DeskAutomationStep,
  names: { personName: string | null; deskName: string | null }
): string => {
  const desk = names.deskName ?? DESK_PLACEHOLDER[step.resourceSource];
  const mode = step.mode === "dedicated" ? "dedicated" : "assigned";

  if (step.action === "assign") {
    // A real name takes the possessive; a placeholder can't, so it moves behind
    // the noun rather than reading "the person on the form's desk".
    return names.personName
      ? `${capitalised(desk)} becomes ${names.personName}'s ${mode} desk.`
      : `${capitalised(desk)} becomes the ${mode} desk of ${PERSON_PLACEHOLDER[step.personSource]}.`;
  }

  const person = names.personName ?? PERSON_PLACEHOLDER[step.personSource];
  return `${capitalised(person)} gives up ${desk} — their ${mode} hold on it is released.`;
};

/**
 * The side effect an assignment carries, or `null` when the step has none.
 *
 * Both assigning actions are *moves* in core: a primary assignment drops every
 * permanent primary assignment the person already has, and a dedicated one
 * releases the dedicated desk they already hold. The freed desk is nowhere on
 * the request, so it can't be named — but leaving the move unsaid is worse,
 * since "what happens to their old desk" is the first thing anyone asks of a
 * seat change.
 */
export const deskAutomationMoveNote = (
  step: DeskAutomationStep,
  /**
   * The desk they hold today, as far as it could be looked up — see
   * {@link vacatedDedicatedDesk}. A known one carries its display name. Core
   * exposes a person's dedicated desks and nothing equivalent for their
   * assignments, so an "assigned" move usually arrives here unknown and keeps
   * the old unnamed wording rather than guessing at a desk.
   */
  vacated: VacatedDesk<string> = { kind: "unknown" }
): string | null => {
  if (step.action !== "assign") return null;
  const hold = step.mode === "dedicated" ? "dedicated desk" : "assigned desk";

  if (vacated.kind === "known") {
    return `${vacated.desk}, their ${hold} today, is freed at the same time.`;
  }
  if (vacated.kind === "none") {
    return "No desk is freed: they hold no other dedicated desk today.";
  }
  return step.mode === "dedicated"
    ? "Any dedicated desk they already hold is freed at the same time."
    : "Any desk they are already assigned to is freed at the same time.";
};

/** What the rule will do to the desk it frees, phrased for a reader deciding. */
export const RELEASED_DESK_ACTION_LABEL: Record<
  WorkplaceRequestReleasedResourceAction,
  string
> = {
  none: "Leave it as it is",
  make_open: "Open it for anyone to book",
  make_unavailable: "Mark it unavailable"
};

/** The same three, as the outcome a reader is looking back on. */
const RELEASED_DESK_OUTCOME_LABEL: Record<
  WorkplaceRequestReleasedResourceAction,
  string
> = {
  none: "left as it was",
  make_open: "opened for anyone to book",
  make_unavailable: "marked unavailable"
};

/**
 * What is about to happen to the freed desk, or `null` when the rule leaves it
 * alone — in which case the move note above has already said everything there
 * is to say.
 */
export const releasedDeskFateNote = (
  step: DeskAutomationStep,
  action: WorkplaceRequestReleasedResourceAction = step.releasedResourceAction
): string | null => {
  if (action === "none") return null;
  const which = step.action === "assign" ? "The desk they leave" : "That desk";
  return `${which} is then ${RELEASED_DESK_OUTCOME_LABEL[action]}.`;
};

/** One freed desk and what the run actually did to it. */
export type ReleasedDeskOutcome = {
  resourceId: string;
  /**
   * Read back from the changes the run recorded, not from the rule it was
   * configured with: an approver may have overridden it, and a desk already in
   * the wanted state records no change at all. Both land on "left as it was",
   * which is what actually happened either way.
   */
  action: WorkplaceRequestReleasedResourceAction;
};

/**
 * Whether the run has already said what it did, which is when the predictions
 * about the freed desk have to stand down.
 *
 * Deliberately not "did it free anything". A finished move can legitimately
 * free nothing — a new hire has no desk to give up — and reports a result
 * carrying no released desks. Reading that as "hasn't run yet" leaves the
 * panel promising, next to a Done badge, that an old desk was freed and
 * opened. A run that is still pending or scheduled, or that failed or was
 * skipped, reports no result at all, and there the prediction is still the
 * most useful thing to show.
 */
export const hasReportedOutcome = (
  run: { result?: WorkplaceRequestWorkflowRunResult } | undefined
): boolean => !!run?.result;

/**
 * The desks a finished run freed, each with what became of it. Empty both for
 * a run that has reported nothing yet and for one that reported freeing no
 * desk — {@link hasReportedOutcome} is what tells those two apart.
 */
export const releasedDeskOutcomes = (
  run: { result?: WorkplaceRequestWorkflowRunResult } | undefined
): ReleasedDeskOutcome[] => {
  const result = run?.result;
  // A reservation run reports a result of its own shape, with no desk in it.
  if (!result || !("user_id" in result)) return [];

  const changesById = new Map(
    (result.updated_resources ?? []).map((updated) => [
      updated.resource_id,
      updated.changes
    ])
  );

  return (result.released_resource_ids ?? []).map((resourceId) => {
    const changes = changesById.get(resourceId) ?? [];
    if (changes.includes("made_unavailable")) {
      return { resourceId, action: "make_unavailable" };
    }
    if (changes.includes("made_open")) {
      return { resourceId, action: "make_open" };
    }
    return { resourceId, action: "none" };
  });
};

/** The outcome written out, e.g. "Desk 12 was opened for anyone to book." */
export const releasedDeskOutcomeSentence = (
  outcome: ReleasedDeskOutcome,
  deskName: string | null
): string =>
  `${deskName ?? "The desk they left"} was freed and ${RELEASED_DESK_OUTCOME_LABEL[outcome.action]}.`;

/**
 * The desk a request gives up, when it holds exactly one release step with a
 * known desk. Drives the "Gives up" row, which is only meaningful when there is
 * a single seat being handed back.
 */
export const releasedResourceId = (
  steps: DeskAutomationStep[]
): string | null => {
  const released = steps.filter(
    (step) => step.action === "release" && step.enabled && step.resourceId
  );
  return released.length === 1 ? released[0].resourceId : null;
};

/**
 * What a move frees, as far as it can be told before it runs. The three are
 * kept apart because they call for different things: a known desk can be
 * named, "none" means the choice about the freed desk is moot and can be put
 * away, and "unknown" is the only case where a generic wording is honest.
 */
export type VacatedDesk<Desk = string> =
  | { kind: "known"; desk: Desk }
  | { kind: "none" }
  | { kind: "unknown" };

/**
 * The desk a dedicated mover gives up: the one dedicated desk they hold today
 * that isn't the desk the rule assigns them. Holding none besides it — a new
 * hire, or someone already sitting at the destination — frees nothing.
 * Holding two makes "the desk they leave" ambiguous, and naming the wrong one
 * is worse than naming none. An "assigned" move is unknown too — core exposes
 * a person's dedicated desks and nothing equivalent for their primary
 * assignments — as is a release, which frees the desk it names rather than
 * one it has to look up.
 */
export const vacatedDedicatedDesk = (
  step: DeskAutomationStep,
  /** Ids of the dedicated desks the person holds; `undefined` until looked up. */
  heldDedicatedResourceIds: string[] | undefined
): VacatedDesk<string> => {
  if (step.action !== "assign" || step.mode !== "dedicated") {
    return { kind: "unknown" };
  }
  if (heldDedicatedResourceIds === undefined) return { kind: "unknown" };
  const held = heldDedicatedResourceIds.filter((id) => id !== step.resourceId);
  if (held.length === 0) return { kind: "none" };
  return held.length === 1
    ? { kind: "known", desk: held[0] }
    : { kind: "unknown" };
};

/** A relocation: the desk a person gives up and the one they take instead. */
export type DeskMove = {
  workflowId: string;
  fromResourceId: string;
  toResourceId: string;
};

/**
 * The relocation a request describes, when there is exactly one it could be —
 * a single assignment step with a known desk, and a single desk it frees.
 * Anything less certain returns `null` and the caller falls back to showing
 * the destination alone; a wrong "from" desk on a map is worse than none.
 *
 * Before the move the freed desk has to be looked up on the person; once the
 * run has reported, the desks it actually released are the record and replace
 * the lookup — including when that record is "none", since a new hire has no
 * desk to give up and is not relocating.
 */
export const deskMoveOf = (
  steps: DeskAutomationStep[],
  runByWorkflowId: ReadonlyMap<
    string,
    { result?: WorkplaceRequestWorkflowRunResult } | undefined
  >,
  vacatedResourceIdOf: (step: DeskAutomationStep) => string | null
): DeskMove | null => {
  const assignments = steps.filter(
    (step) => step.enabled && step.action === "assign" && !!step.resourceId
  );
  if (assignments.length !== 1) return null;
  const step = assignments[0];
  const toResourceId = step.resourceId;
  if (!toResourceId) return null;

  const run = runByWorkflowId.get(step.workflowId);
  let fromResourceId: string | null;
  if (hasReportedOutcome(run)) {
    const result = run?.result;
    // A reservation run reports a result of its own shape, with no desk in it.
    const released =
      result && "user_id" in result ? (result.released_resource_ids ?? []) : [];
    fromResourceId = released.length === 1 ? released[0] : null;
  } else {
    fromResourceId = vacatedResourceIdOf(step);
  }

  if (!fromResourceId || fromResourceId === toResourceId) return null;
  return { workflowId: step.workflowId, fromResourceId, toResourceId };
};
